import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { 
  Clock, 
  Users, 
  TrendingUp, 
  Gift, 
  Star,
  CheckCircle,
  ArrowRight,
  Timer,
  Award,
  Zap,
  Play,
  FileText,
  Smartphone,
  BookOpen,
  Target,
  Crown,
  Globe,
  QrCode
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const PUBLIC_URL = process.env.REACT_APP_PUBLIC_URL || window.location.origin;
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const countries = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'AU', label: 'Australia' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'IT', label: 'Italy' },
  { value: 'ES', label: 'Spain' },
  { value: 'BR', label: 'Brazil' },
  { value: 'MX', label: 'Mexico' },
  { value: 'IN', label: 'India' },
  { value: 'JP', label: 'Japan' },
  { value: 'OTHER', label: 'Other' }
];

const HoldingTankJoin = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    phone: '',
    country: '',
    referrer_code: searchParams.get('ref') || ''
  });
  
  const [countdown, setCountdown] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [registrationResult, setRegistrationResult] = useState(null);

  useEffect(() => {
    loadCountdown();
    loadAnalytics();
    loadLeaderboard();
    
    // Update countdown every second
    const interval = setInterval(loadCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  const loadCountdown = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/holding/countdown`);
      if (response.data.ok) {
        setCountdown(response.data.countdown);
      }
    } catch (error) {
      console.error('Error loading countdown:', error);
    }
  };

  const loadAnalytics = async () => {
    try {
      // We'll get basic stats from leaderboard for now
      const response = await axios.get(`${API_BASE_URL}/api/holding/leaderboard?limit=1`);
      if (response.data.ok) {
        setAnalytics({
          total_users: response.data.count || 0,
          recent_signups: Math.floor(Math.random() * 10) + 5 // Placeholder
        });
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const loadLeaderboard = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/holding/leaderboard?limit=5`);
      if (response.data.ok) {
        setLeaderboard(response.data.leaderboard);
      }
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/holding/join`, formData);
      
      if (response.data.ok) {
        setRegistrationResult(response.data);
        setSuccess(true);
        toast.success('Successfully joined the queue!');
      }
    } catch (error) {
      console.error('Error joining queue:', error);
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const days = Math.floor(seconds / (24 * 60 * 60));
    const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((seconds % (60 * 60)) / 60);
    const secs = seconds % 60;

    return { days, hours, minutes, seconds: secs };
  };

  if (success && registrationResult) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-orange-50 to-green-50">
        <div className="container mx-auto px-4 py-8">
          {/* Success State */}
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <div className="mb-6">
                <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
                <h1 className="text-4xl font-bold text-gray-900 mb-2">Welcome to the Queue!</h1>
                <p className="text-xl text-gray-600">You're officially in line for the BBC Quick List launch</p>
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {/* Queue Position */}
              <Card className="border-2 border-green-200 bg-green-50">
                <CardHeader className="text-center">
                  <CardTitle className="text-2xl text-green-800">Your Queue Position</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="text-6xl font-bold text-green-600 mb-2">
                    #{registrationResult.queue_position}
                  </div>
                  <p className="text-green-700">out of {analytics?.total_users + 1 || 'many'} people in line</p>
                </CardContent>
              </Card>

              {/* Referral Link */}
              <Card className="border-2 border-blue-200 bg-blue-50">
                <CardHeader>
                  <CardTitle className="text-xl text-blue-800">Your Referral Code</CardTitle>
                  <CardDescription>Share this to move up in line!</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center mb-4">
                    <div className="text-2xl font-bold text-blue-600 mb-2">
                      {registrationResult.referral_code}
                    </div>
                    <div className="text-sm text-blue-700 bg-blue-100 p-2 rounded break-all">
                      {PUBLIC_URL}/join?ref={registrationResult.referral_code}
                    </div>
                  </div>
                  <Button 
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    onClick={() => {
                      navigator.clipboard.writeText(`${PUBLIC_URL}/join?ref=${registrationResult.referral_code}`);
                      toast.success('Referral link copied!');
                    }}
                  >
                    Copy Referral Link
                  </Button>
                </CardContent>
              </Card>
            </div>

            <div className="text-center mt-8">
              <Button 
                onClick={() => navigate(`/holding/${registrationResult.user_id}`)}
                className="bg-yellow-400 text-black hover:bg-yellow-500 text-lg px-8 py-3"
              >
                Go to Your Dashboard <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-orange-50 to-green-50">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          
          {/* Hero Section */}
          {/* Video Presentation Section */}
          <div className="mb-16">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Video Section */}
              <div className="relative group">
                <div className="aspect-video bg-gradient-to-br from-blue-600 to-purple-700 rounded-xl overflow-hidden shadow-2xl">
                  <div className="absolute inset-0 bg-black/20"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-white">
                      <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform cursor-pointer">
                        <Play className="w-8 h-8 text-white ml-1" />
                      </div>
                      <h3 className="text-xl font-bold mb-2">Founder's Welcome Message</h3>
                      <p className="text-white/80">Discover the BBC Quick List opportunity</p>
                    </div>
                  </div>
                  {/* Placeholder for actual video */}
                  <div className="absolute bottom-4 left-4 text-white text-sm bg-black/40 px-3 py-1 rounded">
                    ▶ 5:30 - Introduction & Overview
                  </div>
                </div>
              </div>

              {/* Founder Message */}
              <div>
                <div className="inline-flex items-center bg-gradient-to-r from-blue-600/10 to-purple-600/10 text-blue-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <Crown className="w-4 h-4 mr-2" />
                  Founder's Pre-Launch Access
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                  Join the Founding Members
                </h2>
                <p className="text-xl text-gray-600 mb-6">
                  Get exclusive early access to the complete BBC Quick List marketing system with 
                  done-for-you tools, premium training, and priority matrix placement.
                </p>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span className="text-gray-700">Priority matrix positioning</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span className="text-gray-700">Complete done-for-you system</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span className="text-gray-700">Founder recognition & benefits</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mb-12">
            <div className="inline-flex items-center bg-blue-600/10 text-blue-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Timer className="w-4 h-4 mr-2" />
              Limited Time - Founder Registration
            </div>
            <div className="mb-6">
              <h1 className="text-5xl font-bold text-gray-900 mb-4">
                Join the <span className="text-yellow-400">BBC Quick List</span> Revolution
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Be among the first to access the most powerful MLM platform ever created. 
                Get early access, exclusive bonuses, and jump ahead in line by referring friends!
              </p>
            </div>

            {/* Countdown Timer */}
            {countdown && !countdown.is_launched && (
              <div className="bg-gradient-to-r from-yellow-400 via-orange-500 to-green-600 text-white p-6 rounded-lg mb-8">
                <h2 className="text-2xl font-bold mb-4">⏰ Launch Countdown</h2>
                <div className="grid grid-cols-4 gap-4 max-w-md mx-auto">
                  {Object.entries(formatTime(countdown.seconds_remaining)).map(([unit, value]) => (
                    <div key={unit} className="text-center">
                      <div className="text-3xl font-bold">{value}</div>
                      <div className="text-sm uppercase">{unit}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="grid gap-8 lg:grid-cols-3">
            
            {/* Registration Form */}
            <div className="lg:col-span-2">
              <Card className="border-2 border-yellow-400">
                <CardHeader>
                  <CardTitle className="text-2xl">Reserve Your Spot Now</CardTitle>
                  <CardDescription>
                    {formData.referrer_code 
                      ? `🎉 You were referred! You'll get priority placement.`
                      : "Join the queue and start referring friends to move up faster!"
                    }
                  </CardDescription>
                  {/* Invitation Only Notice */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                    <p className="text-sm text-blue-800 font-medium">
                      🔒 <strong>Invitation Only Platform</strong> - To join, request a voucher code below or get referred by an existing member.
                    </p>
                  </div>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Full Name *
                        </label>
                        <Input
                          value={formData.full_name}
                          onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                          placeholder="Your full name"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email Address *
                        </label>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="your@email.com"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Phone Number
                        </label>
                        <Input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="+1 (555) 123-4567"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Country *
                        </label>
                        <Select 
                          value={formData.country} 
                          onValueChange={(value) => setFormData(prev => ({ ...prev, country: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select your country" />
                          </SelectTrigger>
                          <SelectContent>
                            {countries.map((country) => (
                              <SelectItem key={country.value} value={country.value}>
                                {country.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {formData.referrer_code && (
                      <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <Gift className="w-5 h-5 text-green-600" />
                          <span className="font-medium text-green-800">
                            Referral Code: {formData.referrer_code}
                          </span>
                        </div>
                        <p className="text-sm text-green-700 mt-1">
                          You'll get priority placement for being referred!
                        </p>
                      </div>
                    )}

                    <Button 
                      type="submit" 
                      disabled={submitting || !formData.full_name || !formData.email || !formData.country}
                      className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black hover:from-yellow-500 hover:to-orange-600 text-lg py-3"
                    >
                      {submitting ? 'Joining Queue...' : 'Join the Queue Now'} 
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              
              {/* Social Proof */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Users className="w-5 h-5 text-blue-500" />
                    <span>Queue Stats</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">People in Queue</span>
                    <Badge className="bg-blue-100 text-blue-800">
                      {analytics?.total_users || 0}+
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Joined Today</span>
                    <Badge className="bg-green-100 text-green-800">
                      {analytics?.recent_signups || 0}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Launch Status</span>
                    <Badge className="bg-yellow-100 text-yellow-800">
                      <Clock className="w-3 h-3 mr-1" />
                      {countdown ? (countdown.is_launched ? 'Live!' : 'Coming Soon') : 'Loading...'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Top Referrers */}
              {leaderboard.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Award className="w-5 h-5 text-yellow-500" />
                      <span>Top Referrers</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {leaderboard.slice(0, 3).map((user, index) => (
                        <div key={user.user_id} className="flex items-center space-x-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            index === 0 ? 'bg-yellow-100 text-yellow-800' :
                            index === 1 ? 'bg-gray-100 text-gray-800' :
                            'bg-orange-100 text-orange-800'
                          }`}>
                            {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-sm">{user.full_name}</div>
                            <div className="text-xs text-gray-500">{user.referral_count} referrals</div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-medium">#{user.queue_position}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <Button 
                      variant="outline" 
                      className="w-full mt-4"
                      onClick={() => navigate('/leaderboard')}
                    >
                      View Full Leaderboard
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Benefits */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Star className="w-5 h-5 text-purple-500" />
                    <span>Early Access Benefits</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-start space-x-2">
                      <Zap className="w-4 h-4 text-yellow-500 mt-0.5" />
                      <span>Priority access to all features</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <Gift className="w-4 h-4 text-green-500 mt-0.5" />
                      <span>Exclusive launch bonuses</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <TrendingUp className="w-4 h-4 text-blue-500 mt-0.5" />
                      <span>Advanced placement in matrix</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <Award className="w-4 h-4 text-purple-500 mt-0.5" />
                      <span>Founder recognition</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Founder Benefits Showcase */}
          <div className="mt-16 mb-12">
            <div className="text-center mb-12">
              <div className="inline-flex items-center bg-gradient-to-r from-green-600/10 to-blue-600/10 text-green-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Gift className="w-4 h-4 mr-2" />
                Exclusive Founder Benefits
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                What Founders Receive (Done-For-You)
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Complete marketing system with premium tools, training, and support - everything ready to use from day one.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {/* Marketing Tools */}
              <Card className="border-2 border-blue-100 hover:border-blue-200 transition-colors">
                <CardHeader>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
                    <Target className="w-6 h-6 text-blue-600" />
                  </div>
                  <CardTitle className="text-lg">Lead Capture System</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      9+ Professional Templates
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      Custom Lead Capture Pages
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      Lead Management Dashboard
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      Automated Follow-up System
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Link Building Tools */}
              <Card className="border-2 border-purple-100 hover:border-purple-200 transition-colors">
                <CardHeader>
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-3">
                    <Smartphone className="w-6 h-6 text-purple-600" />
                  </div>
                  <CardTitle className="text-lg">Link & QR Tools</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      Link in Bio Builder
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      QR Code Generator
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      Custom Referral Links
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      Social Media Integration
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Training & Content */}
              <Card className="border-2 border-orange-100 hover:border-orange-200 transition-colors">
                <CardHeader>
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-3">
                    <BookOpen className="w-6 h-6 text-orange-600" />
                  </div>
                  <CardTitle className="text-lg">Premium Training</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      3 Enhanced eBooks
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      Marketing Cheat Sheets
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      Progress Tracking System
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      Interactive Quizzes
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Gaming & Profile */}
              <Card className="border-2 border-green-100 hover:border-green-200 transition-colors">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-3">
                    <Award className="w-6 h-6 text-green-600" />
                  </div>
                  <CardTitle className="text-lg">Gaming & Profile</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      Professional Avatar System
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      Gamified Onboarding
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      Tron Wallet Integration
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      Team Building Tools
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Business Integration */}
              <Card className="border-2 border-indigo-100 hover:border-indigo-200 transition-colors">
                <CardHeader>
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-3">
                    <Globe className="w-6 h-6 text-indigo-600" />
                  </div>
                  <CardTitle className="text-lg">Business Integration</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      Custom Business Templates
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      MLM Company Support
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      Lead Redirection System
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      Multi-Brand Management
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Founder Privileges */}
              <Card className="border-2 border-yellow-200 hover:border-yellow-300 transition-colors bg-gradient-to-br from-yellow-50 to-orange-50">
                <CardHeader>
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-3">
                    <Crown className="w-6 h-6 text-yellow-600" />
                  </div>
                  <CardTitle className="text-lg text-yellow-800">Founder Privileges</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-center">
                      <Star className="w-4 h-4 text-yellow-500 mr-2" />
                      Priority Matrix Placement
                    </li>
                    <li className="flex items-center">
                      <Star className="w-4 h-4 text-yellow-500 mr-2" />
                      Founder Recognition Badges
                    </li>
                    <li className="flex items-center">
                      <Star className="w-4 h-4 text-yellow-500 mr-2" />
                      Exclusive Launch Bonuses
                    </li>
                    <li className="flex items-center">
                      <Star className="w-4 h-4 text-yellow-500 mr-2" />
                      VIP Support Access
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Call to Action */}
            <div className="text-center bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Everything Ready From Day One
              </h3>
              <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
                No setup required. No learning curve. Your complete marketing system is ready to use immediately 
                with all tools, training, and integrations pre-configured for maximum results.
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-600">
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
                  <span>Instant Access</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
                  <span>Done-For-You</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
                  <span>Full Support</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
                  <span>Lifetime Updates</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HoldingTankJoin;